import React, { useEffect, useRef, useState, useCallback } from "react";
import ForceGraph from "force-graph";
import { forceX, forceY, forceRadial, forceCollide } from "d3-force";
import {
  Settings2, X, RotateCcw, Search, Plus, Play, ChevronDown,
  ArrowUpRight, ArrowDownLeft,
} from "lucide-react";

/**
 * Obsidian-style force-directed graph of the Vault.
 *
 * Nodes = notes (sized by number of links), tag nodes, attachment nodes
 * (Studio docs), and "ghost" nodes for unresolved [[links]].
 * Links are derived live from [[wiki-links]] in each note's markdown.
 *
 * Control panel (Filters / Groups / Display / Forces) mirrors Obsidian:
 *  - Filters: search, toggle Tags / Attachments / Existing-files-only / Orphans
 *  - Groups: named color rules (query -> color), first match wins
 *  - Display: arrows, text-fade threshold, node size, link thickness
 *  - Forces: center, repel, link strength, link distance + Animate
 *
 * Interactions: zoom/pan, drag nodes, hover-highlight neighbours,
 * click a note to open it. Zoom-aware labels fade in as you zoom.
 */

const TAG_DEFAULT = "#F25C8E";

const DEFAULT_SETTINGS = {
  filters: { search: "", tags: true, attachments: true, existingOnly: true, orphans: true },
  groups: [
    { id: "g-people", query: "path:People", color: "#4ECDC4" },
    { id: "g-meetings", query: "path:Meetings", color: "#7B6FE8" },
    { id: "g-journal", query: "path:Journal", color: "#F2B65A" },
    { id: "g-skills", query: "path:Skills", color: "#4CD6BE" },
    { id: "g-identity", query: "path:Identity", color: "#6B5CE7" },
    { id: "g-studio", query: "path:Document Studio", color: "#F25C8E" },
  ],
  display: { arrows: false, textFade: 0.45, nodeSize: 1, linkThickness: 1 },
  forces: { layout: "circular", center: 0.25, repel: 0.5, link: 0.45, distance: 55 },
};

const STORE_KEY = "ph_graph_settings_v2";

function loadSettings() {
  try {
    const raw = JSON.parse(localStorage.getItem(STORE_KEY) || "null");
    if (!raw) return structuredClone(DEFAULT_SETTINGS);
    return {
      filters: { ...DEFAULT_SETTINGS.filters, ...raw.filters, search: "" },
      groups: Array.isArray(raw.groups) ? raw.groups : DEFAULT_SETTINGS.groups,
      display: { ...DEFAULT_SETTINGS.display, ...raw.display },
      forces: { ...DEFAULT_SETTINGS.forces, ...raw.forces },
    };
  } catch {
    return structuredClone(DEFAULT_SETTINGS);
  }
}

function parseLinks(markdown) {
  const out = [];
  const re = /!?\[\[([^\]|\n#]+)(?:[#|][^\]\n]*)?\]\]/g;
  let m;
  while ((m = re.exec(markdown || "")) !== null) {
    const t = m[1].trim().replace(/\.md$/i, "");
    if (t) out.push(t);
  }
  return out;
}

function matchText(node, q) {
  const s = q.trim().toLowerCase();
  if (!s) return true;
  return (
    (node.label || "").toLowerCase().includes(s) ||
    (node.path || "").toLowerCase().includes(s) ||
    (node.tags || []).some((t) => ("#" + t).toLowerCase().includes(s))
  );
}

function matchGroup(node, query) {
  const q = (query || "").trim().toLowerCase();
  if (!q) return false;
  if (q.startsWith("path:")) return (node.path || "").toLowerCase().includes(q.slice(5).trim());
  if (q.startsWith("file:")) return (node.label || "").toLowerCase().includes(q.slice(5).trim());
  if (q.startsWith("tag:")) {
    const v = q.slice(4).trim();
    if (!v) return node.type === "tag";
    return (node.tags || []).some((t) => ("#" + t).toLowerCase().includes(v)) ||
      (node.type === "tag" && (node.label || "").toLowerCase().includes(v));
  }
  return (node.label || "").toLowerCase().includes(q) || (node.path || "").toLowerCase().includes(q);
}

function colorFor(node, groups) {
  for (const g of groups) {
    if (g.query && g.query.trim() && matchGroup(node, g.query)) return g.color;
  }
  return null;
}

function buildGraphData(docs, filters) {
  const STUDIO = "Document Studio";
  const isAtt = (d) => (d.folder || "").startsWith(STUDIO);
  const byName = new Map();
  for (const d of docs) if (d.name) byName.set(d.name.toLowerCase(), d);

  let nodeList = docs.map((d) => ({
    id: d.id,
    label: d.name,
    type: isAtt(d) ? "attachment" : "note",
    docId: d.id,
    path: (d.folder ? d.folder + "/" : "") + d.name,
    folder: d.folder || "",
    tags: d.tags || [],
    degree: 0,
  }));
  if (!filters.attachments) nodeList = nodeList.filter((n) => n.type !== "attachment");
  const nodeIds = new Set(nodeList.map((n) => n.id));

  const links = [];
  const ghosts = new Map();
  for (const d of docs) {
    if (!nodeIds.has(d.id)) continue;
    for (const t of parseLinks(d.markdown)) {
      const tl = t.toLowerCase();
      const td = byName.get(tl);
      if (td) {
        if (nodeIds.has(td.id) && td.id !== d.id) links.push({ source: d.id, target: td.id });
      } else if (!filters.existingOnly) {
        const gid = "ghost-" + tl;
        if (!ghosts.has(gid)) ghosts.set(gid, { id: gid, label: t, type: "ghost", path: t, folder: "", tags: [], degree: 0 });
        links.push({ source: d.id, target: gid });
      }
    }
  }

  if (filters.tags) {
    const tagNodes = new Map();
    for (const n of nodeList) {
      for (const tg of n.tags || []) {
        const id = "tag-" + tg.toLowerCase();
        if (!tagNodes.has(id)) tagNodes.set(id, { id, label: "#" + tg, type: "tag", path: "#" + tg, folder: "", tags: [tg], degree: 0 });
        links.push({ source: n.id, target: id });
      }
    }
    nodeList = nodeList.concat([...tagNodes.values()]);
  }
  nodeList = nodeList.concat([...ghosts.values()]);

  const idx = new Map(nodeList.map((n) => [n.id, n]));
  for (const n of nodeList) { n.outCount = 0; n.inCount = 0; }
  for (const l of links) {
    const a = idx.get(l.source);
    const b = idx.get(l.target);
    if (a) { a.degree++; a.outCount++; }
    if (b) { b.degree++; b.inCount++; }
  }

  let finalNodes = nodeList;
  let finalLinks = links;
  if (!filters.orphans) {
    finalNodes = nodeList.filter((n) => n.degree > 0);
    const keep = new Set(finalNodes.map((n) => n.id));
    finalLinks = links.filter((l) => keep.has(l.source) && keep.has(l.target));
  }
  return { nodes: finalNodes, links: finalLinks };
}

export default function VaultGraph({ docs, onSelect }) {
  const containerRef = useRef(null);
  const fgRef = useRef(null);
  const dataRef = useRef({ nodes: [], links: [] });
  const hoverRef = useRef({ node: null, nodes: new Set(), links: new Set() });
  const themeRef = useRef({ link: "#6B5CE7", linkDim: "#d4d4d8", label: "#1f2430", ring: "#1f2430", nodeDefault: "#7b7f88", tag: TAG_DEFAULT });
  const fittedRef = useRef(false);

  const [settings, setSettings] = useState(loadSettings);
  const [panelOpen, setPanelOpen] = useState(true);
  const [hoverInfo, setHoverInfo] = useState(null); // { node, x, y } for the preview tooltip
  const settingsRef = useRef(settings);
  settingsRef.current = settings;

  // Persist settings
  useEffect(() => {
    try { localStorage.setItem(STORE_KEY, JSON.stringify(settings)); } catch {}
  }, [settings]);

  const readTheme = useCallback(() => {
    const cs = getComputedStyle(document.documentElement);
    const v = (k, f) => cs.getPropertyValue(k).trim() || f;
    themeRef.current = {
      link: v("--ph-brand-violet", "#6B5CE7"),
      linkDim: v("--hairline", "#d4d4d8"),
      label: v("--ink", "#1f2430"),
      ring: v("--ink", "#1f2430"),
      nodeDefault: v("--ink-dim", "#7b7f88"),
      tag: TAG_DEFAULT,
    };
  }, []);

  const radius = useCallback((node) => {
    const base = node.type === "tag" ? 2.2 : node.type === "ghost" ? 2.4 : 3;
    const r = (base + Math.sqrt(node.degree || 0) * 1.7) * settingsRef.current.display.nodeSize;
    return Math.max(1.6, r);
  }, []);

  const recolor = useCallback(() => {
    const groups = settingsRef.current.groups;
    for (const n of dataRef.current.nodes) n.__color = colorFor(n, groups);
  }, []);

  // Configure the d3 forces for the active layout (force vs circular/radial).
  const applyForces = useCallback(() => {
    const g = fgRef.current;
    if (!g) return;
    const f = settingsRef.current.forces;
    const nodes = dataRef.current.nodes || [];
    const n = nodes.length || 1;
    const maxDeg = dataRef.current.maxDegree || 1;
    try {
      const charge = g.d3Force("charge");
      const link = g.d3Force("link");
      if (link) link.distance(f.distance);
      const center = g.d3Force("center");
      if (center && center.strength) center.strength(0);
      g.d3Force("collide", forceCollide((node) => radius(node) + 2).iterations(1));

      if (f.layout === "circular") {
        // Concentric rings: highly-linked notes pulled to the centre, orphans
        // fanned out to the perimeter. Links are softened so the rings hold —
        // the Obsidian "circular" look.
        const ring = 95 + Math.sqrt(n) * 30;
        g.d3Force("x", null);
        g.d3Force("y", null);
        g.d3Force(
          "radial",
          forceRadial((node) => ring * (1 - (node.degree || 0) / maxDeg) + 32, 0, 0)
            .strength(0.92)
        );
        if (charge) charge.strength(-(f.repel * 240) - 24);
        if (link) link.strength(f.link * 0.22);
      } else {
        g.d3Force("radial", null);
        g.d3Force("x", forceX(0).strength(0.05 + f.center * 0.5));
        g.d3Force("y", forceY(0).strength(0.05 + f.center * 0.5));
        if (charge) charge.strength(-(f.repel * 520) - 18);
        if (link) link.strength(f.link);
      }
      g.d3ReheatSimulation();
    } catch {}
  }, [radius]);

  // ---- Init graph once ----
  useEffect(() => {
    readTheme();
    const el = containerRef.current;
    if (!el) return undefined;
    const g = ForceGraph()(el);
    fgRef.current = g;

    g.backgroundColor("rgba(0,0,0,0)")
      .nodeId("id")
      .nodeLabel(() => "")
      .nodeRelSize(1)
      .linkDirectionalArrowRelPos(1)
      .warmupTicks(20)
      .cooldownTime(4000)
      .autoPauseRedraw(false)
      .d3VelocityDecay(0.32);

    g.linkColor((l) => {
      const h = hoverRef.current;
      const th = themeRef.current;
      if (h.node) return h.links.has(l) ? th.link + "cc" : th.linkDim + "55";
      return th.link + "44";
    });
    g.linkWidth((l) => {
      const t = settingsRef.current.display.linkThickness;
      const h = hoverRef.current;
      return h.node && h.links.has(l) ? t * 2.2 : t * 0.9;
    });
    g.linkDirectionalArrowLength(() => (settingsRef.current.display.arrows ? 3.6 : 0));

    g.nodeCanvasObject((node, ctx, globalScale) => {
      const s = settingsRef.current;
      const h = hoverRef.current;
      const th = themeRef.current;
      const r = radius(node);
      const search = s.filters.search.trim();
      const inHi = h.node ? h.nodes.has(node.id) : true;
      const inSearch = search ? matchText(node, search) : true;
      const dim = (h.node && !inHi) || (search && !inSearch);
      const isHover = h.node && h.node.id === node.id;

      ctx.globalAlpha = dim ? 0.1 : 1;
      ctx.beginPath();
      ctx.arc(node.x, node.y, r, 0, Math.PI * 2);
      const fill = node.__color || (node.type === "tag" ? th.tag : th.nodeDefault);
      ctx.fillStyle = node.type === "ghost" && !node.__color ? th.linkDim : fill;
      ctx.fill();
      if (isHover || (search && inSearch && !dim)) {
        ctx.lineWidth = 1.4 / globalScale;
        ctx.strokeStyle = th.ring;
        ctx.stroke();
      }

      const threshold = 0.2 + s.display.textFade * 2.6;
      let labelAlpha = isHover ? 1 : Math.max(0, Math.min(1, (globalScale - threshold) * 2));
      if (search && inSearch) labelAlpha = Math.max(labelAlpha, 0.95);
      if (labelAlpha > 0.03 && !dim) {
        ctx.globalAlpha = labelAlpha;
        ctx.fillStyle = th.label;
        ctx.font = `${12 / globalScale}px Inter, sans-serif`;
        ctx.textAlign = "center";
        ctx.textBaseline = "top";
        ctx.fillText(node.label, node.x, node.y + r + 2.5 / globalScale);
      }
      ctx.globalAlpha = 1;
    });
    g.nodePointerAreaPaint((node, color, ctx) => {
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(node.x, node.y, radius(node) + 2, 0, Math.PI * 2);
      ctx.fill();
    });

    g.onNodeHover((node) => {
      const h = hoverRef.current;
      h.node = node || null;
      h.nodes = new Set();
      h.links = new Set();
      if (node) {
        h.nodes.add(node.id);
        for (const l of dataRef.current.links) {
          const a = typeof l.source === "object" ? l.source.id : l.source;
          const b = typeof l.target === "object" ? l.target.id : l.target;
          if (a === node.id || b === node.id) { h.links.add(l); h.nodes.add(a); h.nodes.add(b); }
        }
        // Position the preview tooltip at the node's screen coords.
        let pos = null;
        try { pos = g.graph2ScreenCoords(node.x, node.y); } catch { pos = null; }
        setHoverInfo({ node, x: pos ? pos.x : 0, y: pos ? pos.y : 0 });
      } else {
        setHoverInfo(null);
      }
      if (containerRef.current) containerRef.current.style.cursor = node ? "pointer" : "grab";
    });
    g.onNodeDragEnd((node) => { node.fx = node.x; node.fy = node.y; });
    g.onNodeClick((node) => {
      if ((node.type === "note" || node.type === "attachment") && node.docId && onSelect) {
        onSelect(node.docId);
      }
    });
    g.onEngineStop(() => {
      if (!fittedRef.current && dataRef.current.nodes.length) {
        fittedRef.current = true;
        g.zoomToFit(400, 50);
      }
    });

    // size
    const ro = new ResizeObserver(() => {
      if (!containerRef.current) return;
      g.width(containerRef.current.clientWidth);
      g.height(containerRef.current.clientHeight);
    });
    ro.observe(el);
    g.width(el.clientWidth).height(el.clientHeight);

    // theme changes
    const mo = new MutationObserver(readTheme);
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ["class", "style", "data-theme"] });

    return () => {
      ro.disconnect();
      mo.disconnect();
      try { g._destructor && g._destructor(); } catch {}
      fgRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Data (rebuild on docs / structural filters) ----
  useEffect(() => {
    const g = fgRef.current;
    if (!g) return;
    const data = buildGraphData(docs || [], settings.filters);
    data.maxDegree = Math.max(1, ...data.nodes.map((n) => n.degree || 0));
    dataRef.current = data;
    recolor();
    fittedRef.current = false;
    g.graphData(data);
    applyForces();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docs, settings.filters.tags, settings.filters.attachments, settings.filters.existingOnly, settings.filters.orphans]);

  // ---- Recolor on group change ----
  useEffect(() => { recolor(); }, [settings.groups, recolor]);

  // ---- Forces ----
  useEffect(() => { applyForces(); }, [settings.forces, applyForces]);

  // ---- Re-fit the view when the layout mode changes ----
  useEffect(() => { fittedRef.current = false; }, [settings.forces.layout]);

  // ---- mutators ----
  const setFilter = (k, v) => setSettings((s) => ({ ...s, filters: { ...s.filters, [k]: v } }));
  const setDisplay = (k, v) => setSettings((s) => ({ ...s, display: { ...s.display, [k]: v } }));
  const setForce = (k, v) => setSettings((s) => ({ ...s, forces: { ...s.forces, [k]: v } }));
  const updateGroup = (id, patch) => setSettings((s) => ({ ...s, groups: s.groups.map((g) => (g.id === id ? { ...g, ...patch } : g)) }));
  const removeGroup = (id) => setSettings((s) => ({ ...s, groups: s.groups.filter((g) => g.id !== id) }));
  const addGroup = () => setSettings((s) => ({
    ...s,
    groups: [...s.groups, { id: "g-" + Math.random().toString(36).slice(2, 8), query: "", color: "#9b8cff" }],
  }));
  const animate = () => {
    const g = fgRef.current;
    if (!g) return;
    fittedRef.current = false;
    g.d3ReheatSimulation();
  };
  const resetAll = () => setSettings(structuredClone(DEFAULT_SETTINGS));

  const empty = (docs || []).length === 0;

  return (
    <div className="graph-stage" data-testid="vault-graph-stage">
      <div ref={containerRef} className="graph-mount" />

      {hoverInfo && (
        <div
          className="graph-tip"
          style={{ left: hoverInfo.x, top: hoverInfo.y }}
          data-testid="graph-hover-tip"
        >
          <div className="graph-tip-name">{hoverInfo.node.label}</div>
          {hoverInfo.node.type === "tag" ? (
            <div className="graph-tip-folder">tag</div>
          ) : hoverInfo.node.type === "ghost" ? (
            <div className="graph-tip-folder">not created yet</div>
          ) : hoverInfo.node.folder ? (
            <div className="graph-tip-folder">{hoverInfo.node.folder}</div>
          ) : null}
          <div className="graph-tip-stats">
            <span title="Backlinks (incoming)"><ArrowDownLeft size={12} /> {hoverInfo.node.inCount || 0} backlinks</span>
            <span title="Outgoing links"><ArrowUpRight size={12} /> {hoverInfo.node.outCount || 0} outgoing</span>
          </div>
        </div>
      )}

      {empty && (
        <div className="graph-empty" data-testid="graph-empty">
          Your graph is empty. Create notes and link them with <code>[[wiki-links]]</code> to see them connect.
        </div>
      )}

      {!panelOpen && (
        <button className="graph-fab" onClick={() => setPanelOpen(true)} data-testid="graph-controls-toggle" title="Graph settings">
          <Settings2 size={17} />
        </button>
      )}

      {panelOpen && (
        <div className="graph-panel" data-testid="graph-controls-panel">
          <GraphControls
            settings={settings}
            setFilter={setFilter} setDisplay={setDisplay} setForce={setForce}
            updateGroup={updateGroup} removeGroup={removeGroup} addGroup={addGroup}
            animate={animate} reset={resetAll} close={() => setPanelOpen(false)}
          />
        </div>
      )}
    </div>
  );
}

function Switch({ on, onChange, testid }) {
  return (
    <button
      type="button"
      className={`gp-switch ${on ? "on" : ""}`}
      onClick={() => onChange(!on)}
      role="switch"
      aria-checked={on}
      data-testid={testid}
    >
      <span className="gp-knob" />
    </button>
  );
}

function Slider({ value, min, max, step, onChange, testid }) {
  return (
    <input
      type="range"
      className="gp-slider"
      min={min} max={max} step={step}
      value={value}
      onChange={(e) => onChange(parseFloat(e.target.value))}
      data-testid={testid}
    />
  );
}

function Section({ title, children, defaultOpen = true, testid }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="gp-section" data-testid={testid}>
      <button className="gp-section-head" onClick={() => setOpen((o) => !o)}>
        <ChevronDown size={15} className={`gp-caret ${open ? "" : "closed"}`} /> {title}
      </button>
      {open && <div className="gp-section-body">{children}</div>}
    </div>
  );
}

function GraphControls({ settings, setFilter, setDisplay, setForce, updateGroup, removeGroup, addGroup, animate, reset, close }) {
  const { filters, groups, display, forces } = settings;
  return (
    <div className="gp-scroll">
      <div className="gp-top">
        <span className="gp-title">Graph settings</span>
        <div style={{ display: "flex", gap: 4 }}>
          <button className="gp-icon" onClick={reset} title="Reset to defaults" data-testid="graph-reset-btn"><RotateCcw size={14} /></button>
          <button className="gp-icon" onClick={close} title="Close" data-testid="graph-close-controls"><X size={15} /></button>
        </div>
      </div>

      <Section title="Filters" testid="gp-filters">
        <div className="gp-search">
          <Search size={14} />
          <input
            value={filters.search}
            onChange={(e) => setFilter("search", e.target.value)}
            placeholder="Search notes…"
            data-testid="graph-search-input"
          />
        </div>
        <ToggleRow label="Tags" on={filters.tags} onChange={(v) => setFilter("tags", v)} testid="graph-filter-tags" />
        <ToggleRow label="Attachments" on={filters.attachments} onChange={(v) => setFilter("attachments", v)} testid="graph-filter-attachments" />
        <ToggleRow label="Existing files only" on={filters.existingOnly} onChange={(v) => setFilter("existingOnly", v)} testid="graph-filter-existing" />
        <ToggleRow label="Orphans" on={filters.orphans} onChange={(v) => setFilter("orphans", v)} testid="graph-filter-orphans" />
      </Section>

      <Section title="Groups" testid="gp-groups">
        {groups.map((g) => (
          <div className="gp-group-row" key={g.id} data-testid={`graph-group-${g.id}`}>
            <input
              className="gp-group-input"
              value={g.query}
              placeholder="path:People or tag:#x"
              onChange={(e) => updateGroup(g.id, { query: e.target.value })}
              data-testid={`graph-group-query-${g.id}`}
            />
            <label className="gp-color" style={{ background: g.color }}>
              <input type="color" value={g.color} onChange={(e) => updateGroup(g.id, { color: e.target.value })} data-testid={`graph-group-color-${g.id}`} />
            </label>
            <button className="gp-icon" onClick={() => removeGroup(g.id)} title="Remove group" data-testid={`graph-group-remove-${g.id}`}><X size={14} /></button>
          </div>
        ))}
        <button className="gp-btn" onClick={addGroup} data-testid="graph-add-group"><Plus size={14} /> New group</button>
      </Section>

      <Section title="Display" testid="gp-display">
        <div className="gp-slider-row">
          <div className="gp-slider-label">Layout</div>
          <div className="gp-layout" data-testid="graph-layout-toggle">
            <button
              className={forces.layout === "force" ? "active" : ""}
              onClick={() => setForce("layout", "force")}
              data-testid="graph-layout-force"
            >Force</button>
            <button
              className={forces.layout === "circular" ? "active" : ""}
              onClick={() => setForce("layout", "circular")}
              data-testid="graph-layout-circular"
            >Circular</button>
          </div>
        </div>
        <ToggleRow label="Arrows" on={display.arrows} onChange={(v) => setDisplay("arrows", v)} testid="graph-arrows-toggle" />
        <SliderRow label="Text fade threshold" value={display.textFade} min={0} max={1} step={0.01} onChange={(v) => setDisplay("textFade", v)} testid="graph-textfade-slider" />
        <SliderRow label="Node size" value={display.nodeSize} min={0.4} max={2.5} step={0.05} onChange={(v) => setDisplay("nodeSize", v)} testid="graph-nodesize-slider" />
        <SliderRow label="Link thickness" value={display.linkThickness} min={0.3} max={4} step={0.1} onChange={(v) => setDisplay("linkThickness", v)} testid="graph-linkwidth-slider" />
        <button className="gp-btn gp-btn-primary" onClick={animate} data-testid="graph-animate-btn"><Play size={14} /> Animate</button>
      </Section>

      <Section title="Forces" testid="gp-forces" defaultOpen={false}>
        <SliderRow label="Center force" value={forces.center} min={0} max={1} step={0.01} onChange={(v) => setForce("center", v)} testid="graph-center-slider" />
        <SliderRow label="Repel force" value={forces.repel} min={0} max={1} step={0.01} onChange={(v) => setForce("repel", v)} testid="graph-repel-slider" />
        <SliderRow label="Link force" value={forces.link} min={0} max={1} step={0.01} onChange={(v) => setForce("link", v)} testid="graph-link-slider" />
        <SliderRow label="Link distance" value={forces.distance} min={10} max={180} step={1} onChange={(v) => setForce("distance", v)} testid="graph-distance-slider" />
      </Section>
    </div>
  );
}

function ToggleRow({ label, on, onChange, testid }) {
  return (
    <div className="gp-row">
      <span>{label}</span>
      <Switch on={on} onChange={onChange} testid={testid} />
    </div>
  );
}

function SliderRow({ label, value, min, max, step, onChange, testid }) {
  return (
    <div className="gp-slider-row">
      <div className="gp-slider-label">{label}</div>
      <Slider value={value} min={min} max={max} step={step} onChange={onChange} testid={testid} />
    </div>
  );
}
